import type { ReactNode } from 'react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { friendlyAuthError } from '@/data/auth'
import { currentQuarterId, lusakaToday } from '@/lib/periods'
import { greetingFor, longDay, lusakaHour, quarterLabel } from './insights'

const firstName = (full: string) => full.trim().split(/\s+/)[0] ?? full

/** "Good morning, Chileleko" and today's date and quarter, in Lusaka time. */
export function greetingText(fullName: string, now: Date = new Date()): { hello: string; today: string } {
  return {
    hello: `${greetingFor(lusakaHour(now))}, ${firstName(fullName)}`,
    today: `${longDay(lusakaToday(now))} · ${quarterLabel(currentQuarterId(now))}`,
  }
}

/** A dashboard card: title, optional link-ish action on the right, and its own loading/error state. */
export function InsightCard({
  title,
  action,
  className,
  children,
}: {
  title: string
  action?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <Card className={className}>
      <CardBody className="flex h-full flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-card font-semibold text-ink">{title}</h3>
          {action}
        </div>
        {children}
      </CardBody>
    </Card>
  )
}

export function CardLoading() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading">
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-4 w-48" />
    </div>
  )
}

/** A card that failed to load says so, with a retry, without taking the rest of the page down. */
export function CardError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div>
      <p role="alert" className="text-body text-bad">
        {friendlyAuthError(error)}
      </p>
      <Button variant="secondary" size="sm" className="mt-2" onClick={retry}>
        Try again
      </Button>
    </div>
  )
}
